import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { addDoc, collection, serverTimestamp, doc, updateDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, storage } from '../../../firebase';
import { useUserAuth } from '../../../context/userAuthContext';

const useNewActivity = () => {
  const navigate = useNavigate();
  const { user } = useUserAuth();
  
  const [formData, setFormData] = useState({
    title: "",
    isPublic: true,
    location: "",
    coordinates: null,
    locationDetails: "",
    date: "",
    time: "",
    maxAttendees: "1",
    description: "",
    category: "social",
    coverImage: "", // URL de la imagen para preview
    coverImageFile: null // Archivo temporal para subir
  });

  // Estados UI
  const [activeSection, setActiveSection] = useState('basic');
  const [completedSections, setCompletedSections] = useState({
    basic: false,
    locationTime: false,
    details: false
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [sectionErrors, setSectionErrors] = useState({
    basic: "",
    locationTime: "",
    details: ""
  });

  // Authentication check
  useEffect(() => {
    if (!user) {
      navigate('/login');
    }
  }, [user, navigate]);

  // Validation functions
  const validateBasicInfo = () => {
    if (!formData.title.trim()) {
      setSectionErrors(prev => ({ ...prev, basic: "Title is required" }));
      return false;
    }
    if (!formData.maxAttendees || parseInt(formData.maxAttendees) < 1) {
      setSectionErrors(prev => ({ ...prev, basic: "Maximum attendees must be at least 1" }));
      return false;
    }
    setSectionErrors(prev => ({ ...prev, basic: "" }));
    return true;
  };

  const validateLocationTime = () => {
    if (!formData.location.trim() || !formData.coordinates) {
      setSectionErrors(prev => ({ ...prev, locationTime: "Please select a location on the map" }));
      return false;
    }
    if (!formData.date) {
      setSectionErrors(prev => ({ ...prev, locationTime: "Date is required" }));
      return false;
    }
    if (!formData.time) {
      setSectionErrors(prev => ({ ...prev, locationTime: "Time is required" }));
      return false;
    }
    
    const selectedDateTime = new Date(formData.date + 'T' + formData.time);
    if (selectedDateTime < new Date()) {
      setSectionErrors(prev => ({ ...prev, locationTime: "Date and time must be in the future" }));
      return false;
    }

    setSectionErrors(prev => ({ ...prev, locationTime: "" }));
    return true;
  };

  const validateDetails = () => {
    if (!formData.description.trim()) {
      setSectionErrors(prev => ({ ...prev, details: "Description is required" }));
      return false;
    }
    if (formData.description.trim().length < 10) {
      setSectionErrors(prev => ({ ...prev, details: "Description must be at least 10 characters long" }));
      return false;
    }
    setSectionErrors(prev => ({ ...prev, details: "" }));
    return true;
  };

  // Update completed sections based on validation
  useEffect(() => {
    const updateSections = () => {
      setCompletedSections(prev => ({
        ...prev,
        basic: validateBasicInfo(),
        locationTime: validateLocationTime(),
        details: validateDetails()
      }));
    };

    const timeoutId = setTimeout(updateSections, 500);
    return () => clearTimeout(timeoutId);
  }, [formData]);

  // Handler para subir imagen
  const handleImageUpload = async (activityId, imageFile) => {
    if (!imageFile) return null;

    try {
      const storageRef = ref(storage, `activities/${activityId}/cover`);
      await uploadBytes(storageRef, imageFile);
      const imageUrl = await getDownloadURL(storageRef);
      return imageUrl;
    } catch (error) {
      console.error("Error uploading image:", error);
      throw error;
    }
  };

  // Form submission handler
  const handleSubmit = async (e) => {
    if (e) {
      e.preventDefault();
    }
    setError("");

    // Validaciones
    if (!validateBasicInfo()) {
      setActiveSection('basic');
      setError(sectionErrors.basic || "Please complete the Basic Information section");
      return;
    }

    if (!validateLocationTime()) {
      setActiveSection('locationTime');
      setError(sectionErrors.locationTime || "Please complete the Location & Time section");
      return;
    }

    if (!validateDetails()) {
      setActiveSection('details');
      setError(sectionErrors.details || "Please complete the Details section");
      return;
    }

    setIsLoading(true);

    try {
      const dateTime = new Date(formData.date + 'T' + formData.time);

      // Datos base de la actividad
      const activityData = {
        title: formData.title.trim(),
        isPublic: formData.isPublic,
        location: formData.location.trim(),
        coordinates: formData.coordinates,
        locationDetails: formData.locationDetails,
        eventDateTime: dateTime.toISOString(),
        maxAttendees: parseInt(formData.maxAttendees),
        description: formData.description.trim(),
        createdAt: serverTimestamp(),
        creatorUID: user.uid,
        creatorName: user.displayName || 'Anonymous',
        creatorPhoto: user.photoURL || '',
        attendees: [user.uid],
        category: formData.category,
        status: 'active',
        likes: [],
        comments: [],
        coverImage: "" // Campo inicial vacío
      };

      // Crear el documento
      const docRef = await addDoc(collection(db, "activities"), activityData);

      // Si hay imagen, subirla y actualizar el documento
      if (formData.coverImageFile) {
        try {
          const imageUrl = await handleImageUpload(docRef.id, formData.coverImageFile);
          if (imageUrl) {
            const activityRef = doc(db, "activities", docRef.id);
            await updateDoc(activityRef, {
              coverImage: imageUrl
            });
          }
        } catch (imageError) {
          console.error("Error uploading image:", imageError);
        }
      }

      // Navegar a la actividad
      navigate(`/go/${docRef.id}`);
    } catch (error) {
      console.error("Error creating activity:", error);
      setError("Failed to create activity. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  // Input change handler
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    // Manejo especial para las coordenadas
    if (name === 'coordinates') {
      setFormData(prev => ({
        ...prev,
        coordinates: value
      }));
      return;
    }

    // Manejo especial para la ubicación completa
    if (name === 'locationComplete') {
      setFormData(prev => ({
        ...prev,
        location: value.formattedAddress,
        coordinates: value.coordinates,
        locationDetails: value.details
      }));
      return;
    }

    // Manejo especial para la imagen
    if (name === 'coverImage') {
      const file = value.file;
      if (file) {
        setFormData(prev => ({
          ...prev,
          coverImageFile: file,
          coverImage: URL.createObjectURL(file)
        }));
      }
      return;
    }
    
    // Validación para números
    if (type === 'number') {
      const numValue = parseInt(value);
      if (isNaN(numValue) || numValue < 1) {
        return;
      }
    }

    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    setError("");
    setSectionErrors(prev => ({
      ...prev,
      [activeSection]: ""
    }));
  };

  // Handler para maxAttendees
  const handleMaxAttendeesChange = (increment) => {
    setFormData(prev => {
      const currentValue = parseInt(prev.maxAttendees) || 0;
      const newValue = Math.min(Math.max(currentValue + increment, 1), 1000);
      return {
        ...prev,
        maxAttendees: newValue.toString()
      };
    });
  };

  // Toggle section handler
  const handleSectionToggle = (section) => {
    if (section === activeSection) {
      setActiveSection('');
    } else {
      setActiveSection(section);
      setSectionErrors(prev => ({
        ...prev,
        [section]: ""
      }));
    }
  };

  // Cleanup function
  const cleanup = async (activityId) => {
    if (!activityId) return;

    try {
      const storageRef = ref(storage, `activities/${activityId}/cover`);
      await deleteObject(storageRef);
    } catch (error) {
      console.error("Error cleaning up:", error);
    }
  };

  return {
    formData,
    activeSection,
    completedSections,
    isLoading,
    error,
    sectionErrors,
    user,
    handleSubmit,
    handleInputChange,
    handleMaxAttendeesChange,
    handleSectionToggle,
    navigate,
    validateBasicInfo,
    validateLocationTime,
    validateDetails,
    cleanup
  };
};

export default useNewActivity;